import Phaser from "phaser";
import { dropfrontCatalog } from "../data/dropfront-catalog";
import { t } from "../data/strings";
import { shouldRestartOnQuit } from "../flow/entry";
import { SceneKey, assertTransition } from "../flow/game-flow";
import {
  createDropfrontState,
  getHudSnapshot,
  restartDropfront,
  selectCard,
  setPaused,
  tickDropfront,
  tryDrop,
  type DropfrontState,
} from "../features/dropfront";
import { DfPhase } from "../features/dropfront/dropfront-events";
import { DropfrontPresentation } from "../features/dropfront/view/dropfront-presentation";
import { sharedInput } from "../input/input-controller";
import { mountDropfrontHud } from "../ui/huds/dropfront";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";

/**
 * Dropfront — fixed board pieces + frontline links; Art/VFX presentation.
 */
export class DropfrontScene extends Phaser.Scene {
  private state!: DropfrontState;
  private hud!: ReturnType<typeof mountDropfrontHud>;
  private view!: DropfrontPresentation;
  private disposed = false;
  private toast = "";
  private toastT = 0;
  private originX = 0;
  private originY = 0;
  private cellW = 48;
  private cellH = 48;

  constructor() {
    super(SceneKey.Dropfront);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.Dropfront);
    sharedInput.attach();
    clearOverlay();
    getUiLayout().sync();

    this.state = createDropfrontState();
    const viewCfg = dropfrontCatalog.view;
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(viewCfg.colors.bg);
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.setZoom(1);

    const level = dropfrontCatalog.level;
    const topPad = height * viewCfg.layout.topPadRatio;
    const bottomPad = height * viewCfg.layout.bottomPadRatio;
    const boardH = height - topPad - bottomPad;
    const boardW = width * viewCfg.layout.boardWidthRatio;
    const cellW = boardW / level.cols;
    const cellH = boardH / level.rows;
    this.cellW = cellW;
    this.cellH = cellH;
    this.originX = (width - level.cols * cellW) / 2;
    this.originY = topPad;

    this.view = new DropfrontPresentation(this);
    this.view.setMetrics({
      originX: this.originX,
      originY: this.originY,
      cellW,
      cellH,
    });
    this.view.drawBoard(this.state);

    this.hud = mountDropfrontHud({
      onSelectCard: (id) => {
        this.state = selectCard(this.state, id);
        this.view.refreshHighlights(this.state);
      },
      onPauseToggle: () => {
        this.state = setPaused(
          this.state,
          this.state.phase !== DfPhase.Paused,
        );
      },
      onRestart: () => this.restartLevel(),
      onQuit: () => this.quitToTitle(),
    });

    this.input.on("pointerdown", this.onPointer, this);
    this.input.on("pointermove", this.onPointerMove, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());

    this.syncHud();
    this.view.sync(this.state, 0);
  }

  update(_t: number, delta: number): void {
    if (this.disposed) return;
    const fx = dropfrontCatalog.view.fx;
    const dt = Math.min(fx.maxDt, delta / 1000);
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) {
      this.quitToTitle();
      return;
    }

    if (this.toastT > 0) {
      this.toastT -= dt;
      if (this.toastT <= 0) this.toast = "";
    }

    this.state = tickDropfront(this.state, dt);
    this.handleEvents(this.state.events);
    this.view.sync(this.state, dt);
    this.syncHud();
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.disposed) return;
    if (
      this.state.phase !== DfPhase.Playing ||
      !this.state.selectedCard
    ) {
      this.view.setHoverCell(null, null);
      this.view.refreshHighlights(this.state);
      return;
    }
    const cell = this.screenToCell(pointer.worldX, pointer.worldY);
    if (!cell) {
      this.view.setHoverCell(null, null);
    } else {
      this.view.setHoverCell(cell.col, cell.row);
    }
    this.view.refreshHighlights(this.state);
  }

  private onPointer(pointer: Phaser.Input.Pointer): void {
    if (this.disposed) return;
    if (this.state.phase === DfPhase.Won || this.state.phase === DfPhase.Lost) return;
    if (this.state.phase === DfPhase.Paused) return;
    if (!this.state.selectedCard) {
      this.flashToast(t("df.msg.hint"));
      this.syncHud();
      return;
    }
    const cell = this.screenToCell(pointer.worldX, pointer.worldY);
    if (!cell) return;
    const res = tryDrop(this.state, cell.col, cell.row);
    this.state = res.next;
    this.handleEvents(this.state.events);
    this.view.refreshHighlights(this.state);
    this.view.sync(this.state, 0);
    this.syncHud();
  }

  private screenToCell(wx: number, wy: number): { col: number; row: number } | null {
    const { cols, rows } = dropfrontCatalog.level;
    const col = Math.floor((wx - this.originX) / this.cellW);
    const row = Math.floor((wy - this.originY) / this.cellH);
    if (col < 0 || row < 0 || col >= cols || row >= rows) return null;
    return { col, row };
  }

  private handleEvents(events: typeof this.state.events): void {
    this.view.applyEvents(events);
    for (const e of events) {
      if ("messageKey" in e && e.messageKey) {
        const vars =
          "amount" in e && e.amount != null ? { n: e.amount } : undefined;
        try {
          this.flashToast(t(e.messageKey, vars));
        } catch {
          this.flashToast(e.messageKey);
        }
      }
    }
  }

  private flashToast(msg: string): void {
    this.toast = msg;
    this.toastT = dropfrontCatalog.view.fx.toastSec;
  }

  private syncHud(): void {
    const snap = getHudSnapshot(this.state);
    this.hud.sync(this.state, snap, this.toastT > 0 ? this.toast : undefined);
  }

  private restartLevel(): void {
    this.view.destroy();
    this.state = restartDropfront();
    this.toast = "";
    const viewCfg = dropfrontCatalog.view;
    const { width, height } = this.scale;
    const level = dropfrontCatalog.level;
    const topPad = height * viewCfg.layout.topPadRatio;
    const bottomPad = height * viewCfg.layout.bottomPadRatio;
    const boardH = height - topPad - bottomPad;
    const boardW = width * viewCfg.layout.boardWidthRatio;
    const cellW = boardW / level.cols;
    const cellH = boardH / level.rows;
    this.cellW = cellW;
    this.cellH = cellH;
    this.originX = (width - level.cols * cellW) / 2;
    this.originY = topPad;
    this.view = new DropfrontPresentation(this);
    this.view.setMetrics({
      originX: this.originX,
      originY: this.originY,
      cellW,
      cellH,
    });
    this.view.drawBoard(this.state);
    this.view.sync(this.state, 0);
    this.syncHud();
  }

  private quitToTitle(): void {
    if (shouldRestartOnQuit(SceneKey.Dropfront)) {
      assertTransition(SceneKey.Dropfront, SceneKey.Dropfront);
      this.restartLevel();
      this.scene.start(SceneKey.Dropfront);
      return;
    }
    assertTransition(SceneKey.Dropfront, SceneKey.DropfrontSelect);
    this.teardown();
    this.scene.start(SceneKey.DropfrontSelect);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.input.off("pointerdown", this.onPointer, this);
    this.input.off("pointermove", this.onPointerMove, this);
    this.view?.destroy();
    this.hud?.destroy();
    clearOverlay();
  }
}
